import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../lib/utils";

/** Compact page-number window with ellipses, e.g. 1 … 4 5 [6] 7 8 … 42 */
function pageWindow(current, total) {
  const span = 1;
  const pages = new Set([1, total, current]);
  for (let d = 1; d <= span; d++) {
    if (current - d >= 1) pages.add(current - d);
    if (current + d <= total) pages.add(current + d);
  }
  const sorted = Array.from(pages).sort((a, b) => a - b);
  const out = [];
  let prev = 0;
  for (const p of sorted) {
    if (prev && p - prev > 1) out.push("…");
    out.push(p);
    prev = p;
  }
  return out;
}

/**
 * The ONE server-side pagination control — Previous/Next + page numbers,
 * plus a "Showing X-Y of Z" summary. Used anywhere a list is fetched a page
 * at a time from the backend (`page`/`page_size`/`total`/`total_pages`).
 *
 * `loading` dims + disables the controls during a page-transition fetch
 * without unmounting them, so the control doesn't jump/flash between pages.
 */
export default function Pagination({
  page,
  totalPages,
  total,
  pageSize,
  onPageChange,
  loading = false,
  variant = "light", // "light" | "dark" (rider-style dark screens)
  className = "",
  testId = "pagination",
}) {
  if (totalPages <= 1 && total <= pageSize) return null;

  const dark = variant === "dark";
  const canPrev = page > 1 && !loading;
  const canNext = page < totalPages && !loading;
  const rangeFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeTo = Math.min(page * pageSize, total);

  const btnBase = "h-9 min-w-9 px-2.5 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed transition-colors";
  const btnLight = "bg-white border border-neutral-200 text-neutral-700 enabled:hover:bg-neutral-50";
  const btnDark = "bg-white/10 border border-white/10 text-white enabled:hover:bg-white/20";
  const activeLight = "bg-brand-ink text-white border-brand-ink";
  const activeDark = "bg-white text-brand-ink border-white";

  return (
    <div
      className={cn("flex flex-wrap items-center justify-between gap-3 pt-2", loading && "opacity-70", className)}
      data-testid={testId}
    >
      <div className={cn("text-xs font-semibold", dark ? "text-white/60" : "text-neutral-500")} data-testid={`${testId}-summary`}>
        Showing {rangeFrom}–{rangeTo} of {total}
      </div>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => canPrev && onPageChange(page - 1)}
          disabled={!canPrev}
          className={cn(btnBase, dark ? btnDark : btnLight)}
          data-testid={`${testId}-prev`}
        >
          <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">Prev</span>
        </button>
        {pageWindow(page, totalPages).map((p, i) =>
          p === "…" ? (
            <span key={`e${i}`} className={cn("px-1 text-sm", dark ? "text-white/40" : "text-neutral-400")}>…</span>
          ) : (
            <button
              type="button"
              key={p}
              onClick={() => !loading && p !== page && onPageChange(p)}
              disabled={loading}
              className={cn(btnBase, p === page ? (dark ? activeDark : activeLight) : (dark ? btnDark : btnLight))}
              data-testid={`${testId}-page-${p}`}
              aria-current={p === page ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => canNext && onPageChange(page + 1)}
          disabled={!canNext}
          className={cn(btnBase, dark ? btnDark : btnLight)}
          data-testid={`${testId}-next`}
        >
          <span className="hidden sm:inline">Next</span> <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
